import { CheckCircle, Delete, ModeEdit, Person, ReportProblem } from '@mui/icons-material';
import { Avatar, Button, Card, CardActions, CardContent, Container, Grid, IconButton, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { AppModels, AppState, FileInfo, ScanModeViewMode, UserAttrs, UserEmailAttrs, UserPhoneAttrs, appUrl, deleteModel, saveModel, selectUserEmails, selectUserPhones, userEmailSlice, userPhoneSlice, userSelector } from 'cdx-common';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { MainViewLoader } from '../../libraries/MainViewLoader';
import { useSession } from '../../libraries/SessionProvider';
import { appStore } from '../../store';
import { FileLoader } from '../AppItems';
import { MasterViewPage } from '../MasterPage';
import { ModelTextfield } from '../utils/ModelTextField';
import { AWSImageButtonUploader } from '../utils/Uploader';
import { InviteUser } from './InviteUser';
import { NewUserEmail, UserEmailEditor } from './UserEmailEditor';
import { NewUserPhone, UserPhoneEditor } from './UserPhoneEditor';
import { UserRightsView } from './UserRights';

export const UserView = () => {

    const params = useParams();

    if (!params.id) return <></>;

    return <MasterViewPage>
        <MainViewLoader
            path={'user/' + params.id}
            selector={userSelector}
            modelId={params.id}>
            <UserLoaded id={params.id} />
        </MainViewLoader>
    </MasterViewPage>;
};


const UserLoaded = (props: { id: ModelId; }) => {

    const { isSuperAdmin } = useSession();

    const user = (useSelector((state: AppState) => userSelector.selectById(state, props.id)) as UserAttrs);
    const userEmails = useSelector((state: AppState) => selectUserEmails(state, user?.id));
    const userPhones = useSelector((state: AppState) => selectUserPhones(state, user?.id));

    const [editUserEmail, setEditUserEmail] = React.useState<UserEmailAttrs | undefined>(undefined);
    const [newUserEmail, setNewUserEmail] = React.useState<Partial<UserEmailAttrs | undefined>>(undefined);
    const [editUserPhone, setEditUserPhone] = React.useState<UserPhoneAttrs | undefined>(undefined);
    const [newUserPhone, setNewUserPhone] = React.useState<Partial<UserPhoneAttrs | undefined>>(undefined);
    const [userEdited, setUserEdited] = React.useState<UserAttrs>({ ...user });

    const [inviteUser, setInviteUser] = React.useState(false);

    useEffect(() => {
        setUserEdited({ ...user });
    }, []);

    //if (!user) return <></>;

    const onUserImageUpload = async (fileInfo: FileInfo) => {
        await saveModel(AppModels.users, {
            id: user?.id,
            image: fileInfo
        });
    };

    const onDeletePhone = async (phone: UserPhoneAttrs) => {
        if (confirm('Supprimer le numéro de télépĥone ' + phone.phone + ' ?')) {
            const response = await deleteModel(AppModels.userPhones, phone.id);
            if (response) {
                appStore.dispatch(userPhoneSlice.actions.removeOne(phone.id));
            }
        }

    };
    const onDeleteEmail = async (email: UserEmailAttrs) => {
        if (confirm('Supprimer le courriel ' + email.email + ' ?')) {
            const response = await deleteModel(AppModels.userEmails, email.id);
            if (response) {
                appStore.dispatch(userEmailSlice.actions.removeOne(email.id));
            }
        }

    };


    return <>
        {inviteUser && <InviteUser userId={user.id} onClose={() => setInviteUser(false)} />}

        <UserPhoneEditor id={editUserPhone?.id} onClose={() => { setEditUserPhone(undefined); }} />
        <NewUserPhone defaults={newUserPhone} onClose={() => { setNewUserPhone(undefined); }} />
        <UserEmailEditor id={editUserEmail?.id} onClose={() => { setEditUserEmail(undefined); }} />
        <NewUserEmail defaults={newUserEmail} onClose={() => { setNewUserEmail(undefined); }} />
        <Container maxWidth={false}>
            <Grid container>
                <Grid item xs={12}>
                    <Grid container sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Grid item sx={{ width: 100, height: 100, display: 'flex', position: 'relative', alignItems: 'center' }}>
                            {user.imageFileId ? <FileLoader id={String(user.imageFileId)} render={file => <Avatar sx={{ width: '100%', height: '100%' }} src={file.url} />} /> : <Avatar sx={{ width: '100%', height: '100%' }}><Person /></Avatar>}
                            <div className="appearOnParentHover" style={{ textAlign: 'center', position: 'absolute', bottom: 2, width: '100%' }}>
                                <AWSImageButtonUploader onUpload={onUserImageUpload} variant='contained' size='small' />
                            </div>
                        </Grid>
                        <Grid item sx={{ flexGrow: 1, p: 2 }}>
                            <Typography variant='h2'>{user.name}</Typography>
                            <Typography variant='body1'><Link to={appUrl('scanProductsMode', undefined, { id: user.id, type: AppModels.users }, { mode: ScanModeViewMode.DETAILS })}>Sa tenue d'inventaire</Link></Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                    <Card sx={{ m: 1, p: 1 }}>
                        <CardContent>
                            <Typography gutterBottom variant="h4" component="div">
                                Coordonnées et authentification
                            </Typography>
                            <Grid container gap={2}>
                                <Grid item xs={12}>
                                    <ModelTextfield
                                        fullWidth
                                        type='tel'
                                        model={userEdited}
                                        modelType={AppModels.users}
                                        row='name'
                                        value={userEdited.name}
                                        onChange={event => {
                                            setUserEdited(p => {
                                                return {
                                                    ...p,
                                                    name: event.target.value
                                                };
                                            });
                                            saveModel(AppModels.users, {
                                                id: user?.id,
                                                name: event.target.value
                                            });
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Typography gutterBottom variant="h5" component="div">
                                Adresses courriel
                            </Typography>
                            <List sx={{ backgroundColor: 'white' }}>
                                {userEmails.length === 0 ? <ListItem>Aucune</ListItem> : null}
                                {userEmails.map(p => <ListItem divider key={p.id}
                                    secondaryAction={isSuperAdmin && <>
                                        <IconButton onClick={() => { setEditUserEmail({ ...p }); }}><ModeEdit /></IconButton>
                                        <IconButton onClick={() => { onDeleteEmail(p); }}><Delete /></IconButton>
                                    </>}>
                                    <ListItemIcon>
                                        {p.id === user.emailId ? <CheckCircle color='primary' /> : null}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={<>{p.email}</>}
                                    />
                                </ListItem>)}
                            </List>
                            {isSuperAdmin && <CardActions>
                                <Button size="small" color="primary" onClick={() => {
                                    setNewUserEmail({
                                        userId: props.id,
                                    });
                                }}>
                                    Ajouter
                                </Button>
                            </CardActions>}
                            <Typography gutterBottom variant="h5" component="div">
                                Numéros de téléphone
                            </Typography>
                            <List sx={{ backgroundColor: 'white' }}>
                                {userPhones.length === 0 ? <ListItem>Aucun</ListItem> : null}
                                {userPhones.map(p => <ListItem divider key={p.id}
                                    secondaryAction={isSuperAdmin && <>
                                        <IconButton onClick={() => { setEditUserPhone({ ...p }); }}><ModeEdit /></IconButton>
                                        <IconButton onClick={() => { onDeletePhone(p); }}><Delete /></IconButton>
                                    </>}
                                >
                                    <ListItemIcon>
                                        {p.id === user.phoneId ? <CheckCircle color='primary' /> : null}
                                        {p.type === 'emergency' ? <ReportProblem color='error' /> : null}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={'+' + p.phone}
                                        secondary={p.caption}
                                    />
                                </ListItem>)}
                            </List>
                            {isSuperAdmin && <CardActions>
                                <Button size="small" color="primary" onClick={() => {
                                    setNewUserPhone({
                                        userId: props.id,
                                    });
                                }}>
                                    Ajouter
                                </Button>
                            </CardActions>}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                    <Card sx={{ m: 1, p: 1 }}>
                        <CardContent>
                            <Typography gutterBottom variant="h4" component="div">
                                Droits d'accès
                            </Typography>
                            <UserRightsView userId={user.id} />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container></>;
};
